import { useIsFeatureEnabled } from '@shared/core/featureFlags';

import { useGetCashFundsByEventIdQuery } from '../../CashFunds.graphql.generated';

/**
 * Determine whether Cash Funds should be visible for an event,
 * and whether to show a "New" pill badge.
 *
 * `visible` is `true` when either:
 * - the `newCashFundsEnabled` feature flag is on, OR
 * - the event has any cash funds (including archived)
 *
 * `hasNewPill` is `true` only when the feature flag is on — it indicates
 * the feature is being rolled out and should be highlighted.
 *
 * `hasFunds` is `true` only when there are actual cash funds on the event
 * (data-derived, independent of any feature flag). Use this when you need
 * to check fund existence without inheriting flag-based visibility.
 *
 * Feature flags gate *discovery* (showing the entry point to new users),
 * not functionality — admins who created funds must always be able to
 * manage them (including archived ones), and guests must always see
 * configured content.
 *
 * NOTE: Reuses the full GetCashFundsByEventId query with `cache-first`
 * so that cached data is available when the user navigates to the
 * Cash Funds dashboard.
 */
export function useHasCashFunds(eventId: string | undefined): {
  visible: boolean;
  hasNewPill: boolean;
  hasFunds: boolean;
} {
  const [isNewCashFundsEnabled] = useIsFeatureEnabled('newCashFundsEnabled');

  const { data } = useGetCashFundsByEventIdQuery({
    variables: { eventId: eventId ?? '' },
    skip: !eventId,
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
  });

  const funds = data?.getCashFundsByEventId;

  const hasFunds = Array.isArray(funds) && funds.length > 0;

  if (isNewCashFundsEnabled) {
    return { visible: true, hasNewPill: true, hasFunds };
  }

  return { visible: hasFunds, hasNewPill: false, hasFunds };
}
